/**
 * ReviewQueueModal — HITL approval gate for agent write tools. Drop-in
 * replacement for AgentConfirmationModal (same props, same onSubmit contract:
 * an ordered ResumeDecision[] posted to /disasterchat/resume).
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useAors } from 'hooks/useAors';
import { useIncidentObjectives } from 'components/IAP/hooks/incidentObjectiveHooks';
import { useResourceSets } from 'components/IAP/hooks/resourceSetHooks';
import { useIncidentRoster } from 'components/IAP/hooks/incidentRoster/useIncidentRoster';
import { INCIDENT } from 'components/IAP/ICSConstants';
import { unitAllowedDecisions, useReviewQueue } from './useReviewQueue';
import BatchNavigator from './components/BatchNavigator';
import ChangeReview from './components/ChangeReview';
import type { PendingConfirmation, ResumeDecision } from './types';
import type { SelectOption as ConfigSelectOption } from '../confirmationFieldConfig';

interface ReviewQueueModalProps {
  show: boolean;
  confirmations: PendingConfirmation[];
  askText?: string;
  submitting?: boolean;
  /**
   * Submit the resume decisions. Resolve `false` (or reject) if the resume
   * request failed, so the modal can leave the "applying" spinner and let the
   * reviewer retry instead of hanging on it.
   */
  onSubmit: (decisions: ResumeDecision[]) => void | Promise<boolean | void>;
  onClose?: () => void;
}

const hasText = (s?: string): boolean => !!s && s.trim().length > 0;

const ReviewQueueModal: React.FC<ReviewQueueModalProps> = ({
  show,
  confirmations,
  askText,
  submitting = false,
  onSubmit,
  onClose,
}) => {
  const queue = useReviewQueue(confirmations);
  const {
    state,
    currentUnit,
    currentItem,
    currentState,
    allDecided,
    decidedCount,
  } = queue;

  // Re-seed only when a new non-empty batch arrives. The parent passes a fresh
  // `[]` on every idle render, so re-seeding on the empty case would bump
  // `cycle` every render. `cycle` advances only when one real batch replaces
  // another — a genuine re-review after a submit, never the first batch.
  const [seededFor, setSeededFor] = useState(confirmations);
  if (confirmations.length > 0 && seededFor !== confirmations) {
    const isReReview = seededFor.length > 0;
    setSeededFor(confirmations);
    queue.reset(confirmations, isReReview ? state.cycle + 1 : 1);
  }

  const { data: aors } = useAors();
  const aorOptions = useMemo<ConfigSelectOption[]>(
    () =>
      (Array.isArray(aors) ? aors : []).map(
        (a: { id: string; name: string }) => ({
          value: a.id,
          label: a.name,
        })
      ),
    [aors]
  );

  // id→name option lists so linked objective / resources / POC show by name
  // instead of a UUID. Incident-scoped hooks are empty outside an incident.
  const objectiveOptions = useIncidentObjectives(INCIDENT, (objectives) =>
    objectives.map((o) => ({ value: o.objective_id, label: o.name }))
  ).data;
  const { data: resourceSets } = useResourceSets();
  const resourceOptions = useMemo<ConfigSelectOption[]>(
    () =>
      (Array.isArray(resourceSets) ? resourceSets : []).map((r) => ({
        value: r.resource_set_id,
        label: r.name,
      })),
    [resourceSets]
  );
  const pocOptions = useIncidentRoster((roster) =>
    roster.map((m) => ({ value: m.userId, label: m.userName ?? m.userId }))
  ).data;

  const optionSets = useMemo<Record<string, ConfigSelectOption[]>>(
    () => ({
      aor: aorOptions,
      objective: objectiveOptions ?? [],
      resource: resourceOptions,
      poc: pocOptions ?? [],
    }),
    [aorOptions, objectiveOptions, resourceOptions, pocOptions]
  );

  const isBatch = state.units.length > 1;
  const respondActive = hasText(currentState?.respondText);
  const hasEdits = !!(
    currentState?.edits && Object.keys(currentState.edits).length > 0
  );
  const primaryLabel = respondActive
    ? 'Send response'
    : hasEdits
      ? 'Accept edits'
      : 'Accept';

  // Honor the allowed_decisions contract per unit: the confirmation's list
  // (empty/missing = allow all), minus reject for required-on-create fields.
  const allowed =
    currentItem && currentUnit
      ? unitAllowedDecisions(currentItem, currentUnit)
      : undefined;
  const allows = (d: 'approve' | 'edit' | 'reject'): boolean =>
    !allowed || allowed.includes(d);
  const canReject = allows('reject');
  const primaryAllowed = respondActive
    ? canReject
    : hasEdits
      ? allows('edit')
      : allows('approve');

  // Keyboard 1 = primary (accept), 2 = reject. Suppressed while a field has
  // focus so typing doesn't fire a verdict, and gated on allowed_decisions.
  useEffect(() => {
    if (!show || state.phase !== 'review') return undefined;
    const onKey = (e: KeyboardEvent) => {
      const tag = (document.activeElement?.tagName ?? '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (e.key === '1' && primaryAllowed) queue.setVerdict('accepted');
      else if (e.key === '2' && !respondActive && canReject)
        queue.setVerdict('rejected');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    show,
    state.phase,
    state.index,
    respondActive,
    primaryAllowed,
    canReject,
  ]);

  const title = 'PRATUS AI needs your approval';

  const handleSubmit = async () => {
    queue.submit();
    try {
      const ok = await onSubmit(queue.buildDecisions());
      if (ok === false) queue.revertSubmit();
    } catch {
      queue.revertSubmit();
    }
  };

  return (
    <Modal
      show={show}
      centered
      backdrop="static"
      keyboard={false}
      size="lg"
      contentClassName="tw:flex tw:max-h-[88vh] tw:flex-col tw:overflow-hidden"
    >
      <Modal.Header className="tw:flex tw:shrink-0 tw:items-center tw:justify-between">
        <Modal.Title>{title}</Modal.Title>
        {onClose && (
          <button
            type="button"
            className="tw:flex tw:cursor-pointer tw:items-center tw:rounded-md tw:border-none tw:bg-transparent tw:p-1 tw:text-[color:var(--text-weak)] tw:hover:bg-[color:var(--background-light)] tw:hover:text-[color:var(--text-white)]"
            onClick={onClose}
            aria-label="Close and return to chat menu"
            title="Close — you can return to this review later"
          >
            <SharedIcon iconName="close" />
          </button>
        )}
      </Modal.Header>

      <Modal.Body className="tw:flex tw:min-h-0 tw:flex-1 tw:flex-col tw:overflow-hidden">
        {askText && (
          <div className="tw:mb-3 tw:line-clamp-2 tw:shrink-0 tw:whitespace-pre-wrap tw:rounded-lg tw:border tw:border-[color:var(--border-primary)] tw:bg-[color:var(--background-light)] tw:px-3 tw:py-2.5 tw:text-[13px] tw:text-[color:var(--text-white)]">
            {askText}
          </div>
        )}

        {state.phase === 'applying' ? (
          <div className="tw:flex tw:min-h-[180px] tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:gap-3.5 tw:text-center tw:text-[color:var(--text-weak)]">
            <span className="spinner-border" role="status" aria-hidden="true" />
            <p>Writing approved changes &amp; revising your responses…</p>
          </div>
        ) : (
          currentItem &&
          currentUnit && (
            <div className="tw:flex tw:min-h-0 tw:flex-1 tw:gap-4 tw:overflow-hidden">
              {isBatch && (
                <BatchNavigator
                  items={state.items}
                  units={state.units}
                  decisions={state.decisions}
                  activeIndex={state.index}
                  decidedCount={decidedCount}
                  onSelect={queue.goto}
                />
              )}
              <div className="tw:min-h-0 tw:min-w-0 tw:flex-1 tw:overflow-y-auto">
                <ChangeReview
                  item={currentItem}
                  unit={currentUnit}
                  fieldOrdinal={
                    currentUnit.field !== undefined
                      ? state.units
                          .filter(
                            (u) =>
                              u.confirmationIndex ===
                              currentUnit.confirmationIndex
                          )
                          .indexOf(currentUnit) + 1
                      : undefined
                  }
                  fieldCount={
                    currentUnit.field !== undefined
                      ? state.units.filter(
                          (u) =>
                            u.confirmationIndex ===
                            currentUnit.confirmationIndex
                        ).length
                      : undefined
                  }
                  index={state.index}
                  total={state.units.length}
                  cycle={state.cycle}
                  state={currentState}
                  optionSets={optionSets}
                  canRespond={canReject}
                  onEdit={queue.setEdit}
                  onRespond={queue.setRespondText}
                />
              </div>
            </div>
          )
        )}
      </Modal.Body>

      {state.phase === 'review' && (
        <Modal.Footer className="tw:flex tw:shrink-0 tw:flex-nowrap tw:items-center tw:justify-between">
          <div className="tw:flex tw:items-center tw:gap-2.5">
            <StylishNewButton
              className="button--secondary"
              onClick={() => queue.setVerdict('rejected')}
              disabled={respondActive || !canReject}
            >
              Reject
            </StylishNewButton>
            <StylishNewButton
              className="button--primary"
              onClick={() => queue.setVerdict('accepted')}
              disabled={!primaryAllowed}
            >
              {primaryLabel}
            </StylishNewButton>
          </div>
          <div className="tw:flex tw:items-center tw:gap-2.5">
            <span className="tw:text-[12px] tw:text-[color:var(--text-weak)]">
              {decidedCount} of {state.units.length} decided
            </span>
            <StylishNewButton
              className="button--primary"
              onClick={handleSubmit}
              disabled={!allDecided || submitting}
            >
              {submitting ? 'Submitting…' : 'Submit'}
            </StylishNewButton>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default ReviewQueueModal;
